import { Toast } from '@modusign/design-system';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import delay from '@app/libs/delay';
import DocumentStatusPollingGeneralError from '@app/libs/errors/DocumentStatusPollingGeneralError';
import DocumentStatusPollingTimeoutError from '@app/libs/errors/DocumentStatusPollingTimeoutError';
import workspaceService from '@app/libs/workspaceService';
import Status from '@app/store/document/Status';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { getDocumentsStatus } from '@app/store/transport-layer/document/documents/status';

// TODO: @Juyeong-Byeon 네이밍 개선하기 isScheduled가 추가 되면서 useStartImmediateSigningProgress 네이밍이 어색해짐.
export default function useStartImmediateSigningProgress() {
  const dispatch = useDispatch();

  const startImmediateSigningProgress = useCallback(
    async (
      documentId: string,
      userName: string,
      startSigningAs: 'SIGNER' | 'HOST',
      isScheduled: boolean
    ) => {
      const DELAY_TIME = 1000;
      const MAX_DELAY_TIME = 10000;
      const MAX_TIME_OUT = 'MAX_TIME_OUT';

      const pollingResult = await Promise.race([
        pollDocumentStatus(documentId, {
          delayTime: DELAY_TIME,
          maxRetry: MAX_DELAY_TIME / DELAY_TIME,
        }),
        new Promise<typeof MAX_TIME_OUT>((resolve) => {
          setTimeout(() => resolve(MAX_TIME_OUT), MAX_DELAY_TIME);
        }),
      ]);

      if (pollingResult === MAX_TIME_OUT) {
        throw new DocumentStatusPollingTimeoutError(
          isScheduled ? `전송 일시에 서명 요청이 진행됩니다.` : `곧 서명 요청 알림이 전송됩니다.`
        );
      } else if (pollingResult === Status.SCHEDULE_READY) {
        dispatch(modalClose());
        Toast.success({
          description: '문서가 전송 예약되었습니다.',
        });
      } else if (pollingResult === Status.ON_GOING) {
        dispatch(modalClose());
        if (startSigningAs === 'HOST') {
          dispatch(
            modalOpen('DOCUMENTS_DIRECT_TO_CONTRACT_MODAL', {
              content: `${userName}님은 진행자입니다.
        대면서명을 바로 진행하시겠습니까?`,
              confirmText: '대면서명 진행하기',
              documentId,
            })
          );
        } else {
          dispatch(
            modalOpen('DOCUMENTS_DIRECT_TO_CONTRACT_MODAL', {
              content: `${userName}님은 서명자입니다.\n바로 서명하시겠습니까?`,
              confirmText: '서명하기',
              documentId,
            })
          );
        }
      }
    },
    [dispatch]
  );

  return {
    startImmediateSigningProgress,
  };
}

/**
 * 계약 완료 요청 후 완료뷰로 옮겨가기 위해 완료 상태에 대해 폴링(polling)을 진행한다.
 * @params params object
 * @property documentId string - 문서 id
 */
async function pollDocumentStatus(
  documentId: string,
  options: {
    delayTime: number;
    maxRetry: number;
  }
): Promise<Status | null> {
  const { delayTime, maxRetry } = options;
  const STOP_POLLING_STATUS = [Status.ON_GOING, Status.SCHEDULE_READY];

  for (let retry = 0; retry < maxRetry; retry += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const status = (await getDocumentsStatus({
        documentId,
        workspaceId: workspaceService.getWorkspaceId(),
      })) as Status;

      if (STOP_POLLING_STATUS.includes(status)) {
        return status;
      }

      // eslint-disable-next-line no-await-in-loop
      await delay(delayTime);
    } catch (error) {
      if (error.message !== 'document not found') {
        throw new DocumentStatusPollingGeneralError(error.message);
      }
    }
  }

  return null;
}
